import type Activity from '../../model/Activity'
import ActivityIcon from '../icons/ActivityIcon'
import AppIcon from '../icons/AppIcon'
import styles from './ActivitySelect.module.css'

interface ActivityRowProps {
  activity: Activity
  onClick: () => void
  disabled?: boolean
}

interface ActivityTag {
  key: string
  icon: string
  label: string
}

function formatChipDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  })
}

function getActivityTags(activity: Activity): ActivityTag[] {
  const tags: ActivityTag[] = []
  if (activity.oneTimeUse) {
    tags.push({ key: 'once', icon: 'mdi:numeric-1-circle-outline', label: 'One time' })
  }
  if (activity.availableUntilDate != null) {
    tags.push({
      key: 'until',
      icon: 'mdi:clock-outline',
      label: `Open till ${formatChipDate(activity.availableUntilDate)}`,
    })
  }
  return tags
}

function ActivityRow({ activity, onClick, disabled }: ActivityRowProps) {
  const isPartner = activity.group === 'partner'
  const isBonus = activity.group === 'bonus'
  const tags = getActivityTags(activity)

  return (
    <button
      type="button"
      className={[styles.row, isPartner && styles.partner, isBonus && styles.bonus]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      disabled={disabled}
      data-testid="activity-row"
      data-activity-id={activity.id}
    >
      <div className={styles.iconContainer}>
        <ActivityIcon activity={activity} size={27} className={styles.icon} />
      </div>

      <div className={styles.copy}>
        <div className={styles.name}>{activity.title}</div>
        <div className={styles.conv}>{activity.description}</div>
      </div>

      {tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map((tag) => (
            <span key={tag.key} className={styles.tag}>
              <AppIcon icon={tag.icon} size={12} className={styles.tagIcon} />
              {tag.label}
            </span>
          ))}
        </div>
      )}
      <AppIcon icon="mdi:chevron-right" size={20} className={styles.chevron} />
    </button>
  )
}

interface ActivityGroupProps {
  label?: string
  activities: Activity[]
  onSelect: (activity: Activity) => void
  isDisabled: (activity: Activity) => boolean
}

export function ActivityGroupSection({ label, activities, onSelect, isDisabled }: ActivityGroupProps) {
  if (activities.length === 0) return null

  return (
    <section className={styles.section}>
      {label ? <div className={styles.groupLabel}>{label}</div> : null}
      <div className={styles.list}>
        {activities.map((activity) => (
          <ActivityRow
            key={activity.id}
            activity={activity}
            onClick={() => onSelect(activity)}
            disabled={isDisabled(activity)}
          />
        ))}
      </div>
    </section>
  )
}
